import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { zustandSafeLocalStorage } from './safeLocalStorage';

export interface Brand {
    _id: string;
    name: string;
    category?: string;
    logoUrl?: string;
    s3Key?: string;
}

const defaultEndDate = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const defaultStartDate = (): string => `${new Date().getFullYear()}-01-01`;

/** Only persist minimal fields — API / Autocomplete options can include large extras (aliases, metrics, …). */
function slimBrand(brand: unknown): Brand | null {
    if (brand == null || typeof brand !== 'object') return null;
    const b = brand as Record<string, unknown>;
    const _id = b._id;
    const name = b.name;
    if (typeof _id !== 'string' || typeof name !== 'string') return null;
    const out: Brand = { _id, name };
    if (typeof b.category === 'string') out.category = b.category;
    if (typeof b.logoUrl === 'string') out.logoUrl = b.logoUrl;
    if (typeof b.s3Key === 'string') out.s3Key = b.s3Key;
    return out;
}

type BrandExplorerPersisted = {
    selectedBrand: Brand | null;
    startDate: string;
    endDate: string;
};

interface BrandExplorerState {
    selectedBrand: Brand | null;
    setSelectedBrand: (brand: Brand | null) => void;
    startDate: string;
    endDate: string;
    setStartDate: (date: string) => void;
    setEndDate: (date: string) => void;
    reset: () => void;
}

const explorerStorage = createJSONStorage<BrandExplorerPersisted>(() => zustandSafeLocalStorage);

export const useBrandExplorerStore = create<BrandExplorerState>()(
    persist(
        (set) => ({
            selectedBrand: null,
            startDate: defaultStartDate(),
            endDate: defaultEndDate(),
            setSelectedBrand: (brand) => set({ selectedBrand: slimBrand(brand) }),
            setStartDate: (date) => set({ startDate: date }),
            setEndDate: (date) => set({ endDate: date }),
            reset: () => set({ selectedBrand: null, startDate: defaultStartDate(), endDate: defaultEndDate() }),
        }),
        {
            name: 'brand-explorer-state',
            version: 3,
            storage: explorerStorage,
            partialize: (state): BrandExplorerPersisted => ({
                selectedBrand: slimBrand(state.selectedBrand),
                startDate: state.startDate,
                endDate: state.endDate,
            }),
            merge: (persistedState, currentState) => {
                const p = persistedState as Partial<BrandExplorerPersisted> | null | undefined;
                if (!p) return currentState;
                return {
                    ...currentState,
                    startDate: typeof p.startDate === 'string' ? p.startDate : currentState.startDate,
                    endDate: typeof p.endDate === 'string' ? p.endDate : currentState.endDate,
                    selectedBrand: slimBrand(p.selectedBrand),
                };
            },
            migrate: (persisted, _fromVersion): BrandExplorerPersisted => {
                const p = persisted as Partial<BrandExplorerPersisted> | null | undefined;
                return {
                    selectedBrand: slimBrand(p?.selectedBrand ?? null),
                    startDate: typeof p?.startDate === 'string' ? p.startDate : defaultStartDate(),
                    endDate: typeof p?.endDate === 'string' ? p.endDate : defaultEndDate(),
                };
            },
        }
    )
);
